<script lang="ts" setup>
    import '../../../css/choice.css';
    import {popAlert,ModalStyles} from '../../lib/Notification';   
    import {onMounted,computed,ref,reactive} from 'vue';
    import {hideWindowObject,openWindowFromVue} from '../../lib/Models/CommonItemsModel';  
    import {IReceiptData,IMerchantKeyInformation,IPatientCreditCard,IPaySimpleCardRequest,IPaySimpleCardInactivate,IPatientCreditCardRemovals,
        PayeeType,CaptureMethod,DataDisplayLevel,SubmssionType,PaymentSourceDescription,IPaySimple3DS} from '../../lib/Models/PaySimple/PaySimpleCreditCardModel';  
    import {ISearchPatientKey, ISearchPatientItem, ISearchPayerItem,ISearchPayerKey, 
        ISearchPaymentBatchItem, ISearchPaymentBatchKey,ISearchPaymentPlanItem, ISearchPaymentPlanKey} from '../../lib/Models/SearchModel';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import RadioButtonInput from '../../components/RadioButtonInput.vue';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import CurrencyInput from '../../components/CurrencyInput.vue';
    import SearchPatient from '../../components/Search/SearchPatient.vue';
    import SearchPayer from '../../components/Search/SearchPayer.vue';
    import SearchPaymentBatch from '../../components/Search/SearchPaymentBatch.vue';
    import SearchPaymentPlan from '../../components/Search/SearchPaymentPlan.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import ModalDialog from "../../components/ModalDialog.vue";
    import {ErrorReturnMessage,getMerchantKey,getPatientCards,submitPaySimpleCard,removePaySimpleCard,submitPaySimpleCard3DS} from '../../lib/Api/PaySimpleCreditCard';
    import {prettyCurrency} from '../../lib/Formatting/Number'; 
    import {ButtonDisplaySize,ButtonDisplayStyle,LayoutOrientation,ModalSize} from '../../Enums/UiEnums';
    import {ActionButtonStyle} from '../../lib/Types/Types'; 
    import {timeout} from '../../lib/ChoiceUtils'; 
import { blankPageUrl } from '../../lib/OfficeMdUrls';
     
    defineExpose({card3DS});

    type pmt_func_type = ((input: any) => any);

    let pmt : any = undefined; 
    const searchPatientResults = ref<string>("");
    const searchPayerResults = ref<string>("");
    const searchPaymentBatchResults = ref<string>("");
    const searchPaymentPlanResults = ref<string>("");
    const formLoaded = ref<boolean>(false);
    const base_blank_url = blankPageUrl();

    const formData = reactive <
    {       
        merchant: IMerchantKeyInformation,
        receiptRecord: IReceiptData,
        patientCreditCards?: IPatientCreditCard[],
        PatientPaymentMethodId: number, 
        defaultPatientKey?: ISearchPatientKey,
        defaultPayerKey?: ISearchPayerKey,
        defaultPaymentBatchKey?: ISearchPaymentBatchKey,
        defaultPaymentPlanKey?: ISearchPaymentPlanKey,
        captureMethod: {Type: CaptureMethod, PriorType: CaptureMethod, Submission: SubmssionType},
        ccRemovals?: IPatientCreditCardRemovals[],
        submissionData: {Amount: number, CreditCard: boolean},
        disableCaptureButton: boolean, 
        displayLevel: DataDisplayLevel,
        disableExistingCard: boolean,
        isSubmitting?: boolean,  
        displayModalRemove? : boolean,
        card3ds: {ModalConfirm: boolean, URL: string, UsePaymentMethodID: boolean},
        isFormValid?: boolean,  
        componentKey: {PatientKey: number, PayerKey: number, BatchKey: number, PlanKey: number},
        creditCardOnly?: boolean,
        invalidMessageDisplay: {payer: boolean, patient: boolean, batch: boolean, payment: boolean}
    }>({        
        merchant: {validMerchantId: false, paySimpleMerchantKey: "", paymentBatchRequired: false},
        PatientPaymentMethodId: 0, 
        disableCaptureButton: true,
        receiptRecord: {useReceipt: false, requirePayment: true, childCallback: false, paidByType: PayeeType.Patient, copayIndicator: false},
        captureMethod: {Type: CaptureMethod.ManualEntry, PriorType: CaptureMethod.ExistingCard, Submission: SubmssionType.PaymentOnly},
        submissionData: {Amount: 0, CreditCard: false},
        displayLevel: DataDisplayLevel.GatherData,
        disableExistingCard: true,
        displayModalRemove : false,
        card3ds: {ModalConfirm: false, URL: base_blank_url, UsePaymentMethodID: false},
        isFormValid: false,
        componentKey: {PatientKey: 0, PayerKey: 0, BatchKey: 0, PlanKey: 0},
        invalidMessageDisplay: {payer: false, patient: false, batch: false, payment: false}
    });
    const props = withDefaults(defineProps<{  
        saveEnabled: number,
        receiptData : IReceiptData
    }>(),
    {
        saveEnabled: 0,
    });

    onMounted(async () => {
        const queryParams = new URLSearchParams(window.location.search);
        const cc_only = queryParams.get("CCOnly"); 
        await getClientMerchantID();
        if (formData.merchant.validMerchantId) {
            if (cc_only) {
                const patient_id = queryParams.get("PID");
                const patient_name = queryParams.get("Name") ?? "Unknown";
                if (patient_id) {
                    formData.creditCardOnly = true;
                    formData.receiptRecord.useReceipt = true;
                    formData.receiptRecord.paidByType = PayeeType.Patient;
                    formData.receiptRecord.paidByID = +patient_id;
                    formData.receiptRecord.paidByName = patient_name;
                    formData.receiptRecord.requirePayment = false;
                    formData.receiptRecord.childCallback = true;
                    formData.receiptRecord.claimIndicator = "N";
                    formData.receiptRecord.commentIndicator = "N";
                    formData.receiptRecord.procedureIndicator = "N";
                    formData.receiptRecord.diagnosisIndicator = "N";
                    formData.receiptRecord.copayIndicator = false;
                } 
            }
            else {
                formData.receiptRecord = props.receiptData;
            }
            if ((formData.receiptRecord.useReceipt) || (formData.creditCardOnly)) {
                formData.displayLevel = DataDisplayLevel.DisplayReceipt;
                await retrievePatientCreditCards();
            }
        }
        checkInputValues();
    });

    async function getClientMerchantID() : Promise<void>  {
        const respGet = await getMerchantKey(); 
        if (!respGet.success) {  
            await popAlert(respGet.error?.error ?? ErrorReturnMessage.merchant,"PaySimple Merchant",ModalStyles.danger);
            return;
        }
        formData.merchant = 
            {
                validMerchantId: respGet.success.validMerchantId, 
                paySimpleMerchantKey : respGet.success.paySimpleMerchantKey,
                paymentBatchRequired : respGet.success.paymentBatchRequired
            };
        formLoaded.value = true;
        if (formData.merchant.validMerchantId) {
            const pmt_func = await getPaymentMethodTokenizationFunction();
            if (!pmt_func) { 
                await popAlert("Unable to run Payment Method Tokenization","Missing Function",ModalStyles.danger);
                closeWindow();
                return;
            } 
            intitializePaySimple(pmt_func);
            hideWindowObject("LoadingMessage");
        }
        else {
            hideWindowObject("LoadingMessage");
        }
    }
    async function getPaymentMethodTokenizationFunction() : Promise<pmt_func_type| undefined>
    {
        let pmt_func_count = 0;
    
        while(true)
        {
            const pmt_func = (<any>window).paymentMethodTokenization;
            if ('function' === typeof(pmt_func))
                return pmt_func;
            ++pmt_func_count;
            if (pmt_func_count > 25)
                return;
            await timeout(50);			
        }
    }			 
    async function retrievePatientCreditCards() : Promise<void> {
        formData.ccRemovals = [];
        if (!formData.receiptRecord.paidByID) {
            return;
        }
        const respGet = await getPatientCards(formData.receiptRecord.paidByID); 
        if (!respGet.success) {  
            await popAlert(respGet.error?.error ?? ErrorReturnMessage.patientCards,"PaySimple Patient Credit Card List",ModalStyles.danger);
            return;
        }
        formData.patientCreditCards = respGet.success; 
        formData.PatientPaymentMethodId = 0;
        if (formData.patientCreditCards.length > 0) { 
            formData.PatientPaymentMethodId = formData.patientCreditCards[0].paymentMethodId; 
            formData.captureMethod.Type = CaptureMethod.ExistingCard;
            formData.captureMethod.Submission = SubmssionType.PaymentOnly;
        }
        deterimineExistingCardStatus();
    }
    const disableCreditCardsRemoval = computed<boolean>(()=>  {
        if (!formData.ccRemovals?.length) {
            return true;
        }
        if (formData.ccRemovals.filter(item => item.removalFlag && !item.disabled).length == 0) {
            return true;
        }
        return false;
    });
    const displayCaptureButton = computed<boolean>(()=>  {
        if ((!formData.merchant.validMerchantId) || (props.saveEnabled < 1)) {
            return false;
        }
        if (formData.displayLevel == DataDisplayLevel.DisplayReceipt) {
            return true;
        }
        if (formData.displayLevel == DataDisplayLevel.GatherData) {
            return true;
        }
        return false;
    });
    const displayEditButton = computed<boolean>(()=>  {
        if ((!formData.merchant.validMerchantId) || (props.saveEnabled < 1)) {
            return false;
        }
        if ((formData.receiptRecord.useReceipt) || (formData.creditCardOnly)) {
            return false;
        }
        if (formData.displayLevel == DataDisplayLevel.CaptureMethod) {
            return true;
        }
        return false;
    });
    const displayRemoveButton = computed<boolean>(()=>  {  
        if ((!formData.merchant.validMerchantId) || (props.saveEnabled < 1)) {
            return false;
        }   
        if (formData.receiptRecord.paidByType != PayeeType.Patient) {
            return false;
        }
        if ((formData.PatientPaymentMethodId != 0) && (formData.displayLevel == DataDisplayLevel.CaptureMethod) && (formData.captureMethod.Type === CaptureMethod.ExistingCard)) {
            return true;
        }
        return false;
    }); 
    const displaySubmitButton = computed<boolean>(()=>  {
        if ((!formData.merchant.validMerchantId) || (props.saveEnabled < 1)) {
            return false;
        }
        if (formData.captureMethod.Type === CaptureMethod.Swipe) {
            return false;
        }
        if (formData.captureMethod.Type === CaptureMethod.ManualEntry && !formData.isFormValid) {
            return false;
        }
        if (formData.displayLevel == DataDisplayLevel.CaptureMethod) {
            return true;
        }
        return false;
    }); 
    const displayReceiptsButton = computed<boolean>(()=>  {
        if (!formData.merchant.validMerchantId) {
            return false;
        }
        if (!formData.receiptRecord) {
            return false;
        }
        if ((formData.receiptRecord.paidByID ?? 0) > 0 && formData.receiptRecord.paidByType === PayeeType.Patient) {
            return true;
        }
        return false;
    });  
    const displayNewChargeButton = computed<boolean>(()=>  {
        if (!formData.merchant.validMerchantId) {
            return false;
        }
        if ((formData.receiptRecord.useReceipt) || (formData.creditCardOnly)) {
            return false;
        }        
        if (formData.displayLevel == DataDisplayLevel.Completed) {
            return true;
        }
        return false;
    }); 
    const submitText = computed<string>(()=>  {  
        if ((!formData.merchant.validMerchantId) || (props.saveEnabled < 1)) {
            return "Submit";
        }   
        if (formData.captureMethod.Submission == SubmssionType.CardOnly) {
            return "Submit Card";
        }  
        if (formData.captureMethod.Submission == SubmssionType.PaymentOnly) {
            return "Submit Payment";
        }  
        if (formData.captureMethod.Submission == SubmssionType.CardPayment) {
            return "Submit Card and Payment";
        }
        return "Submit";
    });
    const displayPaymentBatchRequired = computed<boolean>(()=>  {
        if ((formData.merchant.paymentBatchRequired) && (!formData.receiptRecord.useReceipt) && (!formData.receiptRecord.paymentBatchID)) {
            return true;
        }
        return false;
    });
    const displayPaymentPlan = computed<boolean>(()=>  {
        if (formData.receiptRecord.paidByType === PayeeType.Payer) {
            return false
        }
        if (!formData.receiptRecord.paidByID) {
            return false
        }
        return true;
    });
    const displayPaymentRequired = computed<boolean>(()=>  {
        if ((formData.receiptRecord.requirePayment) || (formData.receiptRecord.paidByType === PayeeType.Payer)) {
            return true;
        }
        return false;
    });
    function deterimineExistingCardStatus() : void { 
        formData.captureMethod.Type = CaptureMethod.ManualEntry;
        
        if ((!formData.receiptRecord.receiptAmount) || (formData.receiptRecord.receiptAmount < .01)) {
            formData.disableExistingCard = true;
            return; 
        }
        if (formData.receiptRecord.paidByType === PayeeType.Payer) {
            formData.disableExistingCard = true;
            return;
        }
        if ((formData.patientCreditCards?.length ?? 0) == 0) {
            formData.disableExistingCard = true;
            return;
        }
        if (formData.creditCardOnly) {
            formData.disableExistingCard = true;
            return;
        }
        formData.captureMethod.Type = CaptureMethod.ExistingCard;
        formData.captureMethod.Submission = SubmssionType.PaymentOnly;
        formData.disableExistingCard = false;
    }
    function onChangePayeeType() : void {
        formData.receiptRecord.paidByID = undefined;
        formData.receiptRecord.paidByName = undefined;
        checkInputValues();
    }

    async  function onChangePatient(patient: ISearchPatientItem) : Promise<void> { 
        formData.captureMethod.Type = CaptureMethod.ManualEntry;
        if (patient?.patient_id) {
            formData.receiptRecord.paidByID = patient.patient_id;
            formData.receiptRecord.paidByName = patient.patient_name;
            await retrievePatientCreditCards();          
        }
        else {
            formData.receiptRecord.paidByID = undefined;
            formData.receiptRecord.paidByName = undefined;
            formData.patientCreditCards = [];
            formData.PatientPaymentMethodId = 0;
            deterimineExistingCardStatus();
        }
        searchPatientResults.value = JSON.stringify(patient);
        checkInputValues();
    }
    function onChangePayer(payer: ISearchPayerItem) : void {
        if (payer) {
            formData.receiptRecord.paidByID = payer.entity_id;
            formData.receiptRecord.paidByName = payer.payer_name;
        }
        else {
            formData.receiptRecord.paidByID = undefined;
            formData.receiptRecord.paidByName = undefined;
        }
        searchPayerResults.value = JSON.stringify(payer); 
        deterimineExistingCardStatus();
        checkInputValues();
    }
    function onChangePaymentBatch(paymentBatch: ISearchPaymentBatchItem) : void {
        if (paymentBatch) {
            formData.receiptRecord.paymentBatchID = paymentBatch.paymentBatchId;
            formData.receiptRecord.paymentBatchData = paymentBatch.display;
        }
        else {
            formData.receiptRecord.paymentBatchID = undefined;
            formData.receiptRecord.paymentBatchData = undefined; 
        } 
        searchPaymentBatchResults.value = JSON.stringify(paymentBatch);
        checkInputValues();
    }
    function onChangePaymentPlan(paymentPlan: ISearchPaymentPlanItem) : void { 
        if (paymentPlan) {
            formData.receiptRecord.paymentPlanID = paymentPlan.paymentPlanId;
            formData.receiptRecord.paymentPlanData = paymentPlan.display;
            const payment_amount = paymentPlan.display?.split(" * ")[3].replace("$","").replace("(","-").replace(")","").replace("Installment: ","") ;
            if (payment_amount) {
                formData.receiptRecord.receiptAmount = +payment_amount;
            }
        }
        else {
            formData.receiptRecord.paymentPlanID = undefined;
            formData.receiptRecord.paymentPlanData = undefined; 
        } 
        searchPaymentPlanResults.value = JSON.stringify(paymentPlan);
        checkInputValues();
    }
    function checkInputValues() : void {
        formData.invalidMessageDisplay.payment = false;
        formData.invalidMessageDisplay.payer = false;
        formData.invalidMessageDisplay.patient = false;
        formData.invalidMessageDisplay.batch = false;

        if ((!formData.receiptRecord.paidByID) || (!formData.receiptRecord.paidByName)) {
            formData.disableCaptureButton = true;
            if (formData.receiptRecord.paidByType === PayeeType.Payer) {
                formData.invalidMessageDisplay.payer = true;
            }
            else {
                formData.invalidMessageDisplay.patient = true;
            }
            return;
        }
        if ((formData.receiptRecord.requirePayment) || (formData.receiptRecord.paidByType === PayeeType.Payer)) {
            if ((formData.receiptRecord.receiptAmount ?? 0) < .01)  {
                formData.disableCaptureButton = true;
                formData.invalidMessageDisplay.payment = true;
                return;
            }
        }
        if ((formData.merchant.paymentBatchRequired) && (!formData.receiptRecord.useReceipt) && (!formData.receiptRecord.paymentBatchID)) {
            formData.disableCaptureButton = true;
            formData.invalidMessageDisplay.batch = true;
            return;
        }
        formData.disableCaptureButton = false;
    }
    async function closeWindow() : Promise<void>{
        window.close();
    }
    async function executeCaptureMethod() : Promise<void> {
        if ((formData.receiptRecord.receiptAmount ?? 0) == 0) {
            formData.captureMethod.Submission = SubmssionType.CardOnly;
        }
        deterimineExistingCardStatus();
        formData.displayLevel = DataDisplayLevel.CaptureMethod;
    } 
    async function displayEditing() : Promise<void> { 
        clearPMT(); 
        formData.displayLevel = DataDisplayLevel.GatherData; 
    } 
    async function removeCreditCardQuestion() : Promise<void> {
        getDuplicateCards();
        formData.displayModalRemove = true;
    }
    
    async function removeCreditCards() : Promise<void> {
        if (!formData.ccRemovals?.length) {
            return;
        }
        const promises = formData.ccRemovals.map(async item => {
            return removeCreditCard(item);
        });

        await Promise.all(promises);
    } 
    async function removeCreditCard(ccObj : IPatientCreditCardRemovals) : Promise<void> {
        if (ccObj.disabled) {
            return;
        }
        if (!ccObj.removalFlag) {
            return;
        }
        const card_data : IPaySimpleCardInactivate = 
            {
                paymentMethodId: ccObj.paymentMethodId
            };
        ccObj.disabled = true; 
        const respGet = await removePaySimpleCard(card_data); 
        if (!respGet.success) {  
            ccObj.removalMessage = respGet.error?.error ?? ErrorReturnMessage.removeCard;
            return;
        }
        if (respGet.success.errorCode) {  
            ccObj.removalMessage = respGet.success.errorMessage;
            return;
        } 
        ccObj.removalMessage = "Card Removed";
    }
    async function submitCardandPayment() : Promise<void> {
        if (formData.captureMethod.Type === CaptureMethod.ExistingCard) {
            let payment_method_token = "";
            if (formData.patientCreditCards) {
                payment_method_token = formData.patientCreditCards.filter(item => item.paymentMethodId == formData.PatientPaymentMethodId).map(item => item.paySimpleToken)[0];
            }
            if (!payment_method_token) {
                await popAlert("Please select a credit card from the list.","Paysimple Failure",ModalStyles.danger);
                return;
            }
            await cardRequest(payment_method_token);
            return;
        }
        await submitPaymentForm();
    }  
    
    async function submitPaymentForm() : Promise<void>  {
        formData.isSubmitting = true;
        pmt.trigger.submitForm();
        while (true) 
        {
            await timeout(50);
            if (!formData.isSubmitting) {
                break;
            }
        } 
    }
    async function viewReceipts() : Promise<void> {
        if (!formData.receiptRecord.paidByID) {
            return;
        }
        if (!formData.receiptRecord.paidByName) {
            return;
        }
        const window_url = `/officemd/screens/CreditCardReceipts.htm?search=1&PID=${formData.receiptRecord.paidByID}&name=${formData.receiptRecord.paidByName}`;  
        openWindowFromVue("CCReceipts",window_url,{ noTimestamp: "Y" });
    } 
    async function newCharges() : Promise<void> { 
        clearPMT();
        formData.componentKey.PlanKey += 1;  
        formData.componentKey.BatchKey += 1; 
        formData.componentKey.PayerKey += 1; 
        formData.componentKey.PatientKey += 1;  
        formData.receiptRecord = 
            {
                useReceipt: false,
                requirePayment: false, 
                childCallback: false, 
                paidByType: PayeeType.Patient,
                paidByID: undefined,
                paidByName: undefined,
                paymentBatchID: undefined,
                paymentBatchData: undefined,
                receiptAmount: undefined,
                copayIndicator: false
            }; 
        formData.submissionData = {Amount: 0, CreditCard: false};
        formData.captureMethod =  {Type: CaptureMethod.ManualEntry, PriorType: CaptureMethod.ExistingCard, Submission: SubmssionType.PaymentOnly};
        formData.patientCreditCards = [];
        formData.PatientPaymentMethodId = 0;
        formData.displayLevel = DataDisplayLevel.GatherData;  
        checkInputValues();
        deterimineExistingCardStatus(); 
    }
    function setPmtInput() : void {
        pmt.trigger.resetForm();
        if (formData.captureMethod.Type === CaptureMethod.ExistingCard) {  
            formData.captureMethod.Submission = SubmssionType.PaymentOnly; 
        }

        if (formData.captureMethod.PriorType === formData.captureMethod.Type) {
            return;
        }
        formData.captureMethod.PriorType = formData.captureMethod.Type;
        if (formData.captureMethod.Type === CaptureMethod.ExistingCard) {   
            return;
        }
 
        if (formData.captureMethod.PriorType == CaptureMethod.Swipe) {
            pmt.trigger.setMode('cc-swiper');
            return;
        }
        pmt.trigger.setMode('cc-input');
    }
    async function intitializePaySimple(pmt_func :pmt_func_type) : Promise<void> {  
        const merchant_key = formData.merchant.paySimpleMerchantKey;
        /* paymentMethodTokenization is a function on the staging/production import script from paysimple.com, which is why its appears as an error here.*/
        pmt = pmt_func({
            container: document.querySelector('#pmtContainer'),
            merchantKey: merchant_key,
            requireCardholderName: false,
            styles: {
                body: {
                    'backgroundColor': '#fff',
                    'font-size': '8pt',
                },
                container: {
                    default: {
                        'margin-bottom': '15px',
                        'width': '100%',
                    },
                    cardholderName: {
                        'display': 'none',
                    },
                    cardNumber: {
                        'color': 'gray',
                    },
                    label: {
                        default: {
                            'color': '#8f8f8f',
                            'margin-bottom': '5px',
                            'font-size': '12px',
                        },
                        cardNumber: {
                            color: '#0f0',
                        },
                    },
                },
                input: {
                    'border': '1px solid #000',
                    default: {
                        'min-height': '22px',
                        'border-radius': '0px',
                        'border': '#6a8ccb 1px solid',
                        'font-size': '8pt',
                        'color': '#000',
                        'background': '#fff',
                        'padding': '0',
                        'padding-left': '5px',
                        ':focus': {
                            background: '#cccccc',
                        },
                        ':placeholder': {
                            color: 'silver'
                        },
                    },
                },
                validation: {
                    default: {
                        'background': '#f2dede',
                        'color': '#b94a48',
                    },
                },
            }
		});
        pmt.trigger.setMode('cc-input');
        pmt.on('add-payment-method-succeeded', async function (response : any) {  
            await cardRequest(response.paymentMethodToken);
            formData.isSubmitting = false;
            formData.isFormValid = false;
            
        }); 
        pmt.on('add-payment-method-failed', async function (response : any) {
            await popAlert("Unable to process credit card payments at this time. Please try again later.","Paysimple Failure",ModalStyles.danger);
            formData.isSubmitting = false;
            formData.isFormValid = false;
        }); 
        pmt.on('validity-changed', function (response : any) {  
            formData.isFormValid = response.valid;
        });
    } 
    async function cardRequest(paymentMethodToken : string) : Promise<void> { 
        const messageTitle = "PaySimple Submission";
        if (formData.captureMethod.Submission == SubmssionType.CardOnly) {
            formData.receiptRecord.receiptAmount = 0;
        }
        else if  ((formData.receiptRecord.receiptAmount ?? 0) < .01)  {
            await popAlert("Missing payment amount",messageTitle,ModalStyles.danger);
            return;
        } 
        const card_data : IPaySimpleCardRequest = 
            {
                paySimpleToken: paymentMethodToken,
                submssionType: formData.captureMethod.Submission,
                source: (formData.receiptRecord.paymentPlanID ?? 0 > 0 ? PaymentSourceDescription.PmtPlan : PaymentSourceDescription.OfficeEMR),
                receiptData: formData.receiptRecord
            };
        const respGet = await submitPaySimpleCard(card_data);
        if (!respGet.success) {  
            await popAlert(respGet.error?.error ?? ErrorReturnMessage.submitCards,messageTitle,ModalStyles.danger);
            return;
        }
        if ((respGet.success.errorCode) && (respGet.success.redirectURL)) {  
            formData.card3ds = {ModalConfirm: true, URL: respGet.success.redirectURL ?? base_blank_url, UsePaymentMethodID: ((respGet.success.paymentMethodId ?? 0) > 0)};
            return;
        }
        if (respGet.success.errorCode) {  
            await popAlert(respGet.success.errorMessage,messageTitle,ModalStyles.danger);
            return;
        }
        if (respGet.success.paymentMessage) {  
            await popAlert(respGet.success.paymentMessage,messageTitle,ModalStyles.danger);
            return;
        }
        const useCreditCard = ((respGet.success.paymentMethodId ?? 0) > 0);
        clearPMT();
        formData.submissionData = {Amount: respGet.success.paymentAmount ?? 0, CreditCard: useCreditCard};
        if (formData.creditCardOnly) {
            closeWindow();
        }
        else {
            formData.displayLevel = DataDisplayLevel.Completed;
        }
    }
    function clearPMT() : void {
        pmt.trigger.resetForm();
        pmt.trigger.setMode('cc-input');
        formData.captureMethod.PriorType = CaptureMethod.ExistingCard;
    }
    function getDuplicateCards() : void {
        if (!formData.patientCreditCards) {
            return;
        }
        const cardInformation = formData.patientCreditCards.filter(item => item.paymentMethodId === formData.PatientPaymentMethodId)[0].cardInformation;
        const planInformation = formData.patientCreditCards.filter(item => item.paymentMethodId === formData.PatientPaymentMethodId)[0].paymentPlans;
        formData.ccRemovals = [{paymentMethodId: formData.PatientPaymentMethodId, cardInformation: cardInformation, description: "Currently Selected", removalFlag: true, removalMessage: "", disabled: false, planMessage: planInformation, planCount: getPlanCount(planInformation)}];
        const duplicate_cards = formData.patientCreditCards.filter(item => item.duplicateCount > 1 && item.paymentMethodId != formData.PatientPaymentMethodId).map(item => (item.cardInformation)).flat();
        const unique_duplicate_cards = [... new Set(duplicate_cards)]; 
        unique_duplicate_cards.forEach((element) => setDuplicateCards(element));  
    }
    function setDuplicateCards(cardInformation : string) : void {
        const newItems = formData.patientCreditCards?.filter(item => item.cardInformation === cardInformation && item.paymentMethodId != formData.PatientPaymentMethodId).map(item => ({paymentMethodId: item.paymentMethodId, cardInformation: item.cardInformation, description: "Potential Duplicate", removalFlag: true, removalMessage: "", disabled: false, planMessage: item.paymentPlans, planCount: getPlanCount(item.paymentPlans)}));
        if (!newItems) {
            return;
        }
        newItems.shift(); 
        if (formData.ccRemovals) {
            formData.ccRemovals.push(...newItems);
        }
    }
    function getPlanCount(planInformation : string) : number {
        if (planInformation == "") {
            return 0
        }
        return planInformation.split(",").length; 
    }
    async function modalHiddenResponse() : Promise<void> { 
        await retrievePatientCreditCards(); 
        formData.displayModalRemove = false; 
    }
    async function card3DS(activity_id : string,transaction_id : string) : Promise<void> {
        const card_data : IPaySimple3DS = {transactionID: transaction_id, activityID: +activity_id, receiptData: formData.receiptRecord};
        const respGet = await submitPaySimpleCard3DS(card_data);
        const messageTitle = "PaySimple Submission";
        const useCreditCard = formData.card3ds.UsePaymentMethodID;
        
        formData.card3ds = {ModalConfirm: false, URL: base_blank_url, UsePaymentMethodID: false};
        if (!respGet.success) {  
            await popAlert(respGet.error?.error ?? ErrorReturnMessage.submitCards,messageTitle,ModalStyles.danger);
            return;
        }
        if (respGet.success.errorCode) {  
            await popAlert(respGet.success.errorMessage,messageTitle,ModalStyles.danger);
            return;
        }
        if (respGet.success.paymentMessage) {  
            await popAlert(respGet.success.paymentMessage,messageTitle,ModalStyles.danger);
            return;
        } 
        clearPMT();
        formData.submissionData = {Amount: respGet.success.paymentAmount ?? 0, CreditCard: useCreditCard};
        if (formData.creditCardOnly) {
            closeWindow();
        }
        else {
            formData.displayLevel = DataDisplayLevel.Completed;
        }
    }
</script> 
 
<style scoped>
    .ps-card-container-details {
        display: grid;
        grid-template-columns: 2fr 4fr;
        column-gap: 0em;
    }
    .ps-comment {
        white-space: pre-line;
    } 
    .ps-labels {
        color: #204D89;
    }
</style>
<template> 
    <WindowHeaderFooter
    :headerHeight="3.5"
    :footerHeight="3.5"
    currentPathOveride="officemd/App/billing/PaySimpleCreditCard">
        <template v-slot:WindowHeader>
            <div v-if="formLoaded" class="isalus-page-title-large pt-3">PaySimple Credit Card</div>  
        </template>
        <template v-slot:WindowBody>
            <div v-show="formData.card3ds.ModalConfirm" >
                <IFRAME id="fCard3dS" name="fCard3dS" title="PaySimple 3DS" :src=formData.card3ds.URL tabindex="-1" style="width: 95vw; height: 70vh;"></IFRAME> 
            </div>
            <div v-show="!formData.card3ds.ModalConfirm">
                <div v-show="formData.merchant.validMerchantId && formData.displayLevel == DataDisplayLevel.GatherData"> 
                    <div class="row ps-3">
                        <div class="col"> 
                            <span v-if="!formData.receiptRecord.paidByID" class="text-danger">*</span> 
                            <span class="label-isalus-small pe-2">Payee</span>
                            <RadioButtonInput 
                                :value="PayeeType.Patient" 
                                v-model="formData.receiptRecord.paidByType"  
                                :layout="LayoutOrientation.Horz"
                                @update:model-value="onChangePayeeType()">
                                <div class="label-isalus-small">Patient</div>  
                            </RadioButtonInput>
                            <RadioButtonInput 
                                :value="PayeeType.Payer" 
                                v-model="formData.receiptRecord.paidByType"  
                                :layout="LayoutOrientation.Horz"
                                @update:model-value="onChangePayeeType()"> 
                                <div class="label-isalus-small">Payer</div>  
                            </RadioButtonInput>                 
                        </div>
                    </div>
                    <div class="row ps-3 pb-3">
                        <div class="col-6">
                            <div v-if="formData.receiptRecord.paidByType === PayeeType.Patient">
                                <SearchPatient
                                    label=""
                                    :key="formData.componentKey.PatientKey"
                                    :defaultSearchKey="formData.defaultPatientKey" 
                                    :addToBaseClass="'label-isalus-small'"
                                    :enableAutoSearch="true"
                                    :showClearSearch="true"
                                    @onChange="(val) => onChangePatient(val)">                    
                                </SearchPatient>
                                <div v-if="formData.invalidMessageDisplay.patient" class="text-danger label-isalus-small fst-italic">Missing Patient</div>
                            </div>
                            <div v-if="formData.receiptRecord.paidByType === PayeeType.Payer">
                                <SearchPayer
                                    :key="formData.componentKey.PayerKey"
                                    label=""
                                    :defaultSearchKey="formData.defaultPayerKey" 
                                    :addToBaseClass="'label-isalus-small'" 
                                    :showClearSearch="true"
                                    :enableAutoSearch="true"
                                    @onChange="(val) => onChangePayer(val)">
                                </SearchPayer>
                                <div v-if="formData.invalidMessageDisplay.payer" class="text-danger label-isalus-small fst-italic">Missing Payer</div>
                            </div>
                        </div> 
                    </div>
                    <div class="row ps-3 pb-3">
                        <div class="col-6"> 
                            <span v-if="displayPaymentBatchRequired" class="text-danger">*</span>   
                            <SearchPaymentBatch 
                                :key="formData.componentKey.BatchKey"
                                label="Payment Batch"
                                :defaultSearchKey="formData.defaultPaymentBatchKey" 
                                :addToBaseClass="'label-isalus-small'" 
                                :showClearSearch="true"
                                :enableAutoSearch="true"
                                :new-button="true"
                                @onChange="(val) => onChangePaymentBatch(val)">
                            </SearchPaymentBatch>
                            <div v-if="formData.invalidMessageDisplay.batch" class="text-danger label-isalus-small fst-italic" >Missing Payment Batch</div>
                        </div> 
                    </div>
                    
                    <div class="row ps-3 pb-3" v-if="displayPaymentPlan">
                        <div class="col-10">  
                            <SearchPaymentPlan 
                                :patientID="formData.receiptRecord.paidByID"
                                :key="formData.componentKey.PlanKey"
                                label="Payment Plan"
                                :defaultSearchKey="formData.defaultPaymentPlanKey" 
                                :addToBaseClass="'label-isalus-small'" 
                                :showClearSearch="true"
                                :enableAutoSearch="true"
                                :new-button="true"
                                :displayPlanMessage=true
                                @onChange="(val) => onChangePaymentPlan(val)">
                            </SearchPaymentPlan>
                        </div>
                    </div>
                    <div class="row ps-3">
                        <div class="col-6"> 
                            <span v-if="displayPaymentRequired" class="text-danger">*</span>   
                            <span class="label-isalus-small">Payment Amount</span>
                        </div> 
                    </div> 
                    <div class="row ps-3 pb-3"> 
                        <div class="col-3"> 
                            <CurrencyInput 
                                v-model:numberValue="formData.receiptRecord.receiptAmount" 
                                :maxLength=10
                                :startingRange=0.00
                                :endingRange=99999.99
                                :positive=true  
                                placeholder="Payment" 
                                :cssFramework = "'vanilla'"  
                                @update:numberValue = "checkInputValues"
                                @change:numberValue = "checkInputValues"
                            /> 
                            <div v-if="formData.invalidMessageDisplay.payment" class="text-danger label-isalus-small fst-italic">Missing Payment</div>
                        </div> 
                        <div class="col-3">    
                            <Checkbox v-model="formData.receiptRecord.copayIndicator" :title="'Copay'" @update:modelValue="checkInputValues"/>  
                            <span label class="label-isalus-small ps-1 pe-3">Copay</span>   
                        </div> 
                    </div>
                </div>
                <div class="row ps-3" v-if="formData.merchant.validMerchantId && formData.displayLevel == DataDisplayLevel.DisplayReceipt || formData.displayLevel == DataDisplayLevel.CaptureMethod">
                    <div class="col-6">
                        <div class="ps-card-container-details label-isalus-small">
                            <div class="ps-labels fw-bold">ID</div>
                            <div>{{formData.receiptRecord.paidByID}}</div>

                            <div class="ps-labels fw-bold">{{(formData.receiptRecord.paidByType === PayeeType.Patient ? 'Patient' : 'Payer')}}</div>
                            <div>{{formData.receiptRecord.paidByName}}</div>

                            <div v-if="formData.receiptRecord.paymentBatchID" class="ps-labels fw-bold">Payment Batch</div>
                            <div v-if="formData.receiptRecord.paymentBatchID">{{formData.receiptRecord.paymentBatchData}}</div>
                            
                            <div v-if="formData.receiptRecord.paymentPlanID" class="ps-labels fw-bold">Payment Plan</div>
                            <div v-if="formData.receiptRecord.paymentPlanID">{{formData.receiptRecord.paymentPlanData}}</div>

                            <div class="ps-labels fw-bold">Payment Amount</div>
                            <div>{{prettyCurrency(formData.receiptRecord.receiptAmount ?? 0)}}</div>

                            <div class="ps-labels fw-bold">Copay</div>
                            <div>{{(formData.receiptRecord.copayIndicator ? "Yes" : "No")}}</div>

                            <div v-if="formData.receiptRecord.comment" class="ps-labels fw-bold">Comment</div>
                            <div v-if="formData.receiptRecord.comment" class="ps-comment">{{formData.receiptRecord.comment}}</div>
                        </div>
                    </div>
                </div>
                <div class="row  ps-3 pt-2" v-show="formData.merchant.validMerchantId && formData.displayLevel == DataDisplayLevel.CaptureMethod">
                    <p class="text-isalus-primary">Credit Card Capture Method</p> 
                    <div class="row">
                        <div class="col"> 
                            <RadioButtonInput 
                                :disabled="formData.disableExistingCard"
                                :value="CaptureMethod.ExistingCard" 
                                v-model="formData.captureMethod.Type"  
                                :layout="LayoutOrientation.Horz"
                                @update:model-value="setPmtInput()">  
                                <div class="label-isalus-small">Existing Card</div>
                            </RadioButtonInput>
                            <RadioButtonInput 
                                :value="CaptureMethod.ManualEntry" 
                                v-model="formData.captureMethod.Type"  
                                :layout="LayoutOrientation.Horz"
                                @update:model-value="setPmtInput()">                    
                                <div class="label-isalus-small">Manual Entry</div>
                            </RadioButtonInput>
                            <RadioButtonInput 
                                :value="CaptureMethod.Swipe" 
                                v-model="formData.captureMethod.Type"  
                                :layout="LayoutOrientation.Horz"
                                @update:model-value="setPmtInput()">                    
                                <div class="label-isalus-small">Swipe</div>
                            </RadioButtonInput>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col"> 
                            <div v-show="!(formData.captureMethod.Type === CaptureMethod.ExistingCard)" id="pmtContainer"></div> 
                        </div> 
                    </div>
                    <div class="row">
                        <div class="col" v-show="((formData.patientCreditCards?.length ?? 0) > 0 && formData.captureMethod.Type === CaptureMethod.ExistingCard)"> 
                            <select v-model="formData.PatientPaymentMethodId" class="form-select">
                                <option v-for="(cardRecord,index) in formData.patientCreditCards" :value="cardRecord.paymentMethodId">{{cardRecord.cardInformation}}</option>
                            </select>
                        </div>
                    </div>
                    <div class="row" v-if="formData.receiptRecord.paidByType === PayeeType.Patient && formData.captureMethod.Type !== CaptureMethod.ExistingCard">
                        <div class="col"> 
                            <RadioButtonInput 
                                :value="SubmssionType.PaymentOnly" 
                                :disabled="((formData.receiptRecord.receiptAmount ?? 0) < .01)"
                                v-model="formData.captureMethod.Submission"  
                                :layout="LayoutOrientation.Horz">                    
                                <div class="label-isalus-small">Submit Payment</div>
                            </RadioButtonInput>
                            <RadioButtonInput 
                                :value="SubmssionType.CardPayment" 
                                :disabled="((formData.receiptRecord.receiptAmount ?? 0) < .01)"
                                v-model="formData.captureMethod.Submission"  
                                :layout="LayoutOrientation.Horz">                    
                                <div class="label-isalus-small">Save Card with Payment Submission</div>
                            </RadioButtonInput>   
                            <RadioButtonInput  
                                :value="SubmssionType.CardOnly" 
                                :disabled="formData.receiptRecord.requirePayment"
                                v-model="formData.captureMethod.Submission"  
                                :layout="LayoutOrientation.Horz">  
                                <div class="label-isalus-small">Save Card without Payment Submission</div>
                            </RadioButtonInput>
                        </div> 
                    </div>
                </div>
                <div v-if="formData.merchant.validMerchantId && formData.displayLevel == DataDisplayLevel.Completed">
                    <div v-if="(formData.submissionData.Amount ?? 0 > 0)" class="label-isalus-small">Payment {{(formData.receiptRecord.paidByName == "" ? "" : "from " + formData.receiptRecord.paidByName)}} for {{prettyCurrency(formData.submissionData.Amount)}} completed successfully</div>
                    <div v-if="formData.submissionData.CreditCard" class="label-isalus-small">Credit Card Saved{{(formData.receiptRecord.paidByName == "" ? "" : " for " + formData.receiptRecord.paidByName)}}.</div>
                </div>
                <div v-if="!formData.merchant.validMerchantId && formLoaded" class="text-danger">
                    A Merchant ID has not yet been assigned to your account.  Please contact your administrator in order to set up this ID.
                </div>
            </div>
        </template>
        <template v-slot:WindowFooter>
            <div class="row">
                <div class="col-2">
                    <span class="pe-2" v-if="displayEditButton">
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Edit"
                            :text="'Edit Details'"
                            :size="ButtonDisplaySize.Small" 
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                            :spinText="'Editing'"
                            :performAction="displayEditing"/>
                    </span>        
                </div>                
                <div class="col-10 text-end">
                    <span class="pe-2" v-if="displayCaptureButton">
                        <ActionButtonV2 
                                :disabled="formData.disableCaptureButton"
                                :action="ActionButtonStyle.Complete"
                                :text="'Next: Capture Method'"
                                :size="ButtonDisplaySize.Small" 
                                :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                                :spinText="'Capturing'"
                                :performAction="executeCaptureMethod"/>
                    </span>         
                    <span class="pe-2" v-if="displayRemoveButton"> 
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Trash"
                            :text="'Remove Credit Card'"
                            :size="ButtonDisplaySize.Small" 
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                            :spinText="'Removing'"
                            :performAction="removeCreditCardQuestion"/>              
                    </span>       
                    <span class="pe-2" v-if="displaySubmitButton">
                        <ActionButtonV2 
                                :action="ActionButtonStyle.Complete"
                                :text="submitText"
                                :size="ButtonDisplaySize.Small" 
                                :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                                :spinText="'Submitting'"
                                :performAction="submitCardandPayment"/>
                    </span>        
                    <span class="pe-2" v-if="displayReceiptsButton"> 
                        <ActionButtonV2 
                                :action="ActionButtonStyle.Open"
                                :text="'View Receipts'"
                                :size="ButtonDisplaySize.Small" 
                                :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                                :spinText="'Viewing'"
                                :performAction="viewReceipts"/>
                    </span>         
                    <span class="pe-2" v-if="displayNewChargeButton">
                        <ActionButtonV2 
                                :action="ActionButtonStyle.Open"
                                :text="'Create New Payment'"
                                :size="ButtonDisplaySize.Small" 
                                :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                                :spinText="'New'"
                                :performAction="newCharges"/>
                    </span>   
                    <span class="pe-2">
                        <ActionButtonV2 
                                :action="ActionButtonStyle.Close"
                                :size="ButtonDisplaySize.Small" 
                                :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                                :spinText="'Closing'"
                                :performAction="closeWindow"/>
                    </span>   
                </div> 
            </div>
        </template>
    </WindowHeaderFooter> 
    <ModalDialog 
        :show="formData.displayModalRemove"
        @hidden="modalHiddenResponse"  
        :size="ModalSize.Medium"
        :closeOnClickOutside=false>
        <template #title>
            Credit Card Removal
        </template>
        <div> 
            Remove the following credit card(s)?
            <div style="height: 12rem; overflow: auto;">
                <div v-for="(ccRecord,index) in formData.ccRemovals" :value="ccRecord.paymentMethodId">
                    <Checkbox class="ps-1" v-model="ccRecord.removalFlag" :disabled="ccRecord.disabled" />  
                    <span label class="label-isalus-small ps-1 pe-3">{{ccRecord.description}} {{ccRecord.paymentMethodId}}:  {{ccRecord.cardInformation}}</span>  
                    <div v-if="ccRecord.planCount > 0" class="label-isalus-small ps-1">
                        <span class="pe-1 ps-4">Connected to</span>
                        <span v-if="ccRecord.planCount > 1" class="fw-bolder pe-1 text-primary">{{ccRecord.planCount}}</span>
                        <span v-if="ccRecord.planCount > 1">Payment Plans</span>
                        <span v-if="ccRecord.planCount == 1">Payment Plan</span>
                        <span class="ps-1">{{ccRecord.planMessage}}</span>
                    </div>
                    <div class="label-isalus-small ps-1 text-danger">{{ccRecord.removalMessage}}</div>
                    
                </div>
            </div>
        </div>
        <template #footer>
            <span class="pe-2">
                <ActionButtonV2 
                    :disabled="disableCreditCardsRemoval"
                    :action="ActionButtonStyle.Trash"
                    :size="ButtonDisplaySize.Small" 
                    :text="'Remove selected Credit Card(s)'"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Removing'"
                    :performAction="removeCreditCards"/>
            </span>
            <span class="pe-2">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Close"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Closing'"
                    :performAction="modalHiddenResponse"/>
            </span>
        </template>
    </ModalDialog> 
</template>